import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import useDocumentHead from '../hooks/useDocumentHead';
import database from '../database';
import { parsePgnToLines, formatPgn } from '../utils/pgnParser';
import { slugify } from '../utils/urlUtils';
import { GameLogic } from '../services/GameLogic';
import chessAudio from '../utils/chessAudio';
import AppHeader from '../components/layout/AppHeader';
import Icon from '../components/ui/Icon';
import {
  BOARD_LIGHT_SQUARE, BOARD_DARK_SQUARE, LAST_MOVE_COLOR, SELECTED_COLOR,
  MOVE_DOT, CAPTURE_RING, NOTATION_LIGHT, NOTATION_DARK
} from '../theme';

const inputClass = 'input';

const buildGame = (moves) => {
  const game = new Chess();
  moves.forEach(move => game.move(move));
  return game;
};

/**
 * Course Creator Page
 * Single Responsibility: Building a community course (lines played on the board or imported from PGN)
 */
function CourseCreatorPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [course, setCourse] = useState({ name: '', description: '', authorName: '', playerColor: 'white', difficulty: '' });
  const [lines, setLines] = useState([]);

  // Line being built on the board
  const [draftMoves, setDraftMoves] = useState([]);
  const [draftComments, setDraftComments] = useState({});
  const [draftName, setDraftName] = useState('');
  const [draftTrap, setDraftTrap] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);

  const [pgnText, setPgnText] = useState('');
  const [importMessage, setImportMessage] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useDocumentHead({
    title: 'Create a Course - Eseky',
    description: 'Build your own chess opening course on Eseky: play your lines on the board or import a PGN.',
    canonical: '/course-creator/new'
  });

  const game = useMemo(() => buildGame(draftMoves), [draftMoves]);

  const updateCourse = (field) => (e) => setCourse(prev => ({ ...prev, [field]: e.target.value }));

  // ==================== BOARD ====================
  const playMove = useCallback((from, to) => {
    const result = GameLogic.validateMove(game, from, to);
    if (!result.success) {
      chessAudio.playIllegalSound();
      return false;
    }
    chessAudio.playMoveSound();
    setDraftMoves(prev => [...prev, result.move.san]);
    setSelectedSquare(null);
    return true;
  }, [game]);

  const onPieceDrop = useCallback(({ sourceSquare, targetSquare }) => playMove(sourceSquare, targetSquare), [playMove]);

  const onSquareClick = useCallback(({ square }) => {
    const piece = game.get(square);
    if (selectedSquare && selectedSquare !== square && playMove(selectedSquare, square)) return;
    setSelectedSquare(piece && piece.color === game.turn() && selectedSquare !== square ? square : null);
  }, [game, selectedSquare, playMove]);

  const squareStyles = useMemo(() => {
    const styles = {};
    const lastMove = game.history({ verbose: true }).pop();
    if (lastMove) {
      styles[lastMove.from] = { backgroundColor: LAST_MOVE_COLOR };
      styles[lastMove.to] = { backgroundColor: LAST_MOVE_COLOR };
    }
    if (!selectedSquare) return styles;
    styles[selectedSquare] = { backgroundColor: SELECTED_COLOR };
    GameLogic.getPossibleMoves(game, selectedSquare).forEach(square => {
      styles[square] = {
        ...styles[square],
        backgroundImage: game.get(square) ? CAPTURE_RING : MOVE_DOT,
        cursor: 'pointer'
      };
    });
    return styles;
  }, [game, selectedSquare]);

  const resetDraft = () => {
    setDraftMoves([]);
    setDraftComments({});
    setDraftName('');
    setDraftTrap(false);
    setSelectedSquare(null);
  };

  const addDraftLine = () => {
    if (draftMoves.length === 0) {
      setError('Play at least one move on the board to add a line.');
      return;
    }
    const pgn = formatPgn(draftMoves);
    if (lines.some(line => line.pgn === pgn)) {
      setError('This line is already in the course.');
      return;
    }
    const comments = Object.fromEntries(
      Object.entries(draftComments).filter(([key, text]) => Number(key) <= draftMoves.length && text.trim())
    );
    setLines(prev => [...prev, {
      key: `${Date.now()}-${prev.length}`,
      name: draftName.trim() || `Line ${prev.length + 1}`,
      pgn,
      moves: draftMoves,
      comments,
      trap: draftTrap
    }]);
    setError('');
    resetDraft();
  };

  const loadLine = (line) => {
    setDraftMoves(line.moves);
    setDraftComments(line.comments || {});
    setDraftName(`${line.name} (variation)`);
    setDraftTrap(line.trap);
    setSelectedSquare(null);
  };

  const updateLine = (key, changes) => setLines(prev => prev.map(line => (line.key === key ? { ...line, ...changes } : line)));
  const removeLine = (key) => setLines(prev => prev.filter(line => line.key !== key));

  // ==================== IMPORT ====================
  const importPgn = (text) => {
    const { lines: imported, errors } = parsePgnToLines(text);
    const existing = new Set(lines.map(line => line.pgn));
    const fresh = imported.filter(line => !existing.has(line.pgn));
    setLines(prev => [...prev, ...fresh.map((line, index) => ({
      ...line,
      key: `${Date.now()}-import-${index}`,
      trap: false
    }))]);
    setImportMessage({
      text: `${fresh.length} line(s) imported${imported.length > fresh.length ? `, ${imported.length - fresh.length} duplicate(s) skipped` : ''}.`,
      errors
    });
    if (fresh.length > 0) setPgnText('');
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => importPgn(String(reader.result));
    reader.readAsText(file);
    e.target.value = '';
  };

  // ==================== PUBLISH ====================
  const publish = async () => {
    if (!course.name.trim()) {
      setError('The course needs a name.');
      return;
    }
    if (lines.length === 0) {
      setError('Add at least one line before publishing.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await database.createCourse({
        name: course.name.trim(),
        description: course.description.trim() || null,
        author_name: course.authorName.trim() || null,
        player_color: course.playerColor,
        difficulty: course.difficulty || null,
        lines: lines.map(line => ({
          name: line.name.trim() || 'Line',
          pgn: line.pgn,
          comments: Object.keys(line.comments || {}).length ? line.comments : null,
          trap: line.trap
        }))
      });
      navigate(`/${slugify(course.name.trim())}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const moveList = useMemo(() => {
    const pairs = [];
    for (let i = 0; i < draftMoves.length; i += 2) {
      pairs.push(`${i / 2 + 1}. ${draftMoves[i]}${draftMoves[i + 1] ? ` ${draftMoves[i + 1]}` : ''}`);
    }
    return pairs;
  }, [draftMoves]);

  const sectionTitle = (icon, text) => (
    <h2 className="flex items-center gap-2 text-sm font-bold text-ink-100 mb-3">
      <Icon name={icon} size={16} className="text-brand-400" /> {text}
    </h2>
  );

  return (
    <div className="min-h-screen min-h-[100dvh]">
      <AppHeader>
        <Link to="/" className="btn-ghost btn-icon" title="Back to courses"><Icon name="arrowLeft" size={18} /></Link>
        <h1 className="text-sm sm:text-base font-bold text-ink-100 truncate">New course</h1>
      </AppHeader>

      <main className="max-w-7xl mx-auto p-4 lg:p-6 flex flex-col lg:flex-row gap-6">
        {/* Board */}
        <div className="flex-1 flex flex-col items-center gap-4">
          <div
            className="aspect-square rounded-xl overflow-hidden shadow-board ring-1 ring-black/30"
            style={{ width: 'min(92vw, calc(100dvh - 330px), 620px)', minWidth: 'min(92vw, 300px)' }}
          >
            <Chessboard
              options={{
                id: 'course-creator',
                position: game.fen(),
                onPieceDrop,
                onSquareClick,
                squareStyles,
                boardOrientation: course.playerColor,
                allowDragging: true,
                lightSquareStyle: BOARD_LIGHT_SQUARE,
                darkSquareStyle: BOARD_DARK_SQUARE,
                lightSquareNotationStyle: NOTATION_LIGHT,
                darkSquareNotationStyle: NOTATION_DARK,
                animationDurationInMs: 160
              }}
            />
          </div>

          <div className="card w-full max-w-[620px] p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 text-sm text-ink-200 min-h-[2.5rem] font-mono break-words leading-relaxed">
                {moveList.length ? moveList.join(' ') : <span className="text-ink-500 font-sans">Play both sides on the board to build a line.</span>}
              </div>
              <button onClick={() => setDraftMoves(prev => prev.slice(0, -1))} disabled={!draftMoves.length} className="btn-secondary btn-icon" title="Undo last move" aria-label="Undo last move"><Icon name="undo" size={16} /></button>
              <button onClick={resetDraft} disabled={!draftMoves.length} className="btn-secondary btn-icon" title="Clear the board" aria-label="Clear the board"><Icon name="rotate" size={16} /></button>
            </div>
            <label className="label mt-4" htmlFor="draft-comment">
              Comment after {draftMoves.length ? moveList[moveList.length - 1] : 'the start position'} <span className="normal-case tracking-normal font-medium text-ink-500">(optional)</span>
            </label>
            <textarea
              id="draft-comment"
              value={draftComments[draftMoves.length] || ''}
              onChange={(e) => setDraftComments(prev => ({ ...prev, [draftMoves.length]: e.target.value }))}
              rows={2}
              placeholder="Explain the idea behind this move..."
              className={inputClass}
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <input value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder={`Line ${lines.length + 1}`} className={inputClass} aria-label="Line name" />
              <label className="chip cursor-pointer justify-center whitespace-nowrap py-2.5">
                <input type="checkbox" checked={draftTrap} onChange={(e) => setDraftTrap(e.target.checked)} className="accent-brand-500" /> Trap
              </label>
              <button onClick={addDraftLine} className="btn-primary whitespace-nowrap"><Icon name="plus" size={16} /> Add line</button>
            </div>
          </div>
        </div>

        {/* Course form */}
        <div className="w-full lg:w-[420px] flex flex-col gap-4">
          <section className="card p-4 flex flex-col gap-4">
            {sectionTitle('book', 'Course')}
            <div className="-mt-1">
              <label className="label" htmlFor="course-name">Name</label>
              <input id="course-name" value={course.name} onChange={updateCourse('name')} placeholder="My Sicilian Defense" className={inputClass} />
            </div>
            <div>
              <label className="label" htmlFor="course-description">Description</label>
              <textarea id="course-description" value={course.description} onChange={updateCourse('description')} rows={2} placeholder="Learn the key ideas and traps in the Sicilian" className={inputClass} />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="label" htmlFor="course-author">Author</label>
                <input id="course-author" value={course.authorName} onChange={updateCourse('authorName')} placeholder="Your name" className={inputClass} />
              </div>
              <div className="flex-1">
                <label className="label" htmlFor="course-level">Level</label>
                <select id="course-level" value={course.difficulty} onChange={updateCourse('difficulty')} className={inputClass}>
                  <option value="">—</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>
            <div>
              <span className="label">Play as</span>
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-ink-900 border border-ink-700">
                {['white', 'black'].map(color => (
                  <button
                    key={color}
                    onClick={() => setCourse(prev => ({ ...prev, playerColor: color }))}
                    className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      course.playerColor === color ? 'bg-ink-700 text-white' : 'text-ink-400 hover:text-ink-100'
                    }`}
                    aria-pressed={course.playerColor === color}
                  >
                    <span className={`w-3 h-3 rounded-full ring-1 ${color === 'white' ? 'bg-ink-100 ring-ink-300' : 'bg-ink-950 ring-ink-500'}`} />
                    {color === 'white' ? 'White' : 'Black'}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Import */}
          <section className="card p-4">
            {sectionTitle('upload', 'Import a PGN')}
            <textarea
              value={pgnText}
              onChange={(e) => setPgnText(e.target.value)}
              rows={4}
              placeholder="Paste a PGN: variations become separate lines, comments are kept."
              className={`${inputClass} font-mono text-xs`}
              aria-label="PGN"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={() => importPgn(pgnText)} disabled={!pgnText.trim()} className="btn-secondary flex-1">Import</button>
              <button onClick={() => fileInputRef.current?.click()} className="btn-secondary flex-1"><Icon name="upload" size={16} /> Upload .pgn</button>
              <input ref={fileInputRef} type="file" accept=".pgn,text/plain" onChange={handleFile} className="hidden" />
            </div>
            {importMessage && (
              <div className="mt-3 text-xs space-y-1">
                <div className="text-brand-300 font-semibold">{importMessage.text}</div>
                {importMessage.errors.map(message => <div key={message} className="text-amber-300">{message}</div>)}
              </div>
            )}
          </section>

          {/* Lines */}
          <section className="card p-4">
            {sectionTitle('sliders', `Lines (${lines.length})`)}
            {lines.length === 0 && <div className="text-sm text-ink-500">No lines yet. Play one on the board or import a PGN.</div>}
            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto -mr-1 pr-1">
              {lines.map(line => (
                <div key={line.key} className="rounded-xl bg-ink-900 border border-ink-700 p-2.5">
                  <div className="flex gap-1 items-center">
                    <input value={line.name} onChange={(e) => updateLine(line.key, { name: e.target.value })} className="flex-1 min-w-0 bg-transparent text-sm font-semibold text-ink-100 rounded-md px-1.5 py-1 focus:outline-none focus:bg-ink-800" aria-label="Line name" />
                    <button
                      onClick={() => updateLine(line.key, { trap: !line.trap })}
                      className={`badge ${line.trap ? 'bg-amber-500/15 text-amber-300' : 'text-ink-500 hover:text-ink-300'}`}
                      title="Mark as a trap"
                      aria-pressed={line.trap}
                    >
                      Trap
                    </button>
                    <button onClick={() => loadLine(line)} className="btn-ghost btn-icon py-1.5" title="Load on the board to create a variation" aria-label="Edit"><Icon name="wrench" size={14} /></button>
                    <button onClick={() => removeLine(line.key)} className="btn-ghost btn-icon py-1.5 hover:text-rose-300" title="Remove line" aria-label="Remove"><Icon name="trash" size={14} /></button>
                  </div>
                  <div className="text-xs text-ink-400 font-mono mt-1 px-1.5 break-words">{line.pgn}</div>
                </div>
              ))}
            </div>
          </section>

          {error && <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</div>}

          <button onClick={publish} disabled={isSubmitting} className="btn-primary py-3 text-base">
            {isSubmitting ? 'Publishing…' : <>Publish course <Icon name="arrowRight" size={18} /></>}
          </button>
        </div>
      </main>
    </div>
  );
}

export default CourseCreatorPage;
